import type { AlphaDesignVersion, AlphaSafeZone } from "@nlc/shared-types";

import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ChevronLeft, Download, Loader2 } from "lucide-react";
import { useReducer, useState } from "react";

import { AccessibleIcon } from "@/components/accessible-icon";
import { Button } from "@/components/button";
import { FullPageLoader } from "@/components/fullpage-loader";
import { usePageTitle } from "@/hooks/usePageTitle";

import type { EditorTool } from "./editorTools";

import { alphaDesignName } from "../alphaDesignName";
import { ALPHA_MOCKED_STYLES } from "../generation/alphaMockedStyles";
import { useAlphaRunsQuery } from "../hooks/useAlphaRunsQuery";
import { SafeZoneOverlay, SafeZoneToggle } from "../SafeZoneOverlay";
import { AnnotationLayer } from "./AnnotationLayer";
import { EditorToolRail } from "./EditorToolRail";
import { GenerateButton } from "./GenerateButton";
import { useAlphaDesignVersionsQuery } from "./hooks/useAlphaDesignVersionsQuery";
import { useCreateAlphaEdit } from "./hooks/useCreateAlphaEdit";
import { useDownloadAlphaVersion } from "./hooks/useDownloadAlphaVersion";
import { InstructionsPanel } from "./InstructionsPanel";
import {
  initialMarksHistory,
  marksReducer,
  type NewMark,
} from "./marksHistory";
import { MarksList } from "./MarksList";
import { VersionsPanel } from "./VersionsPanel";

export function AlphaEditorPage() {
  const params = useParams({ strict: false }) as {
    designId?: string;
    requestId?: number | string;
  };
  const { version } = useSearch({ strict: false }) as { version?: string };

  return (
    <AlphaEditor
      designId={params.designId ?? ""}
      requestId={Number(params.requestId)}
      versionId={version}
    />
  );
}

export function AlphaEditor({
  designId,
  requestId,
  versionId,
}: {
  designId: string;
  requestId: number;
  versionId?: string;
}) {
  const navigate = useNavigate();
  const versionsQuery = useAlphaDesignVersionsQuery(requestId, designId);
  const runsQuery = useAlphaRunsQuery(requestId);
  const versions = versionsQuery.data?.versions;
  const openVersion = versions
    ? (versions.find(({ id }) => id === versionId) ??
      versions.find(({ parentId }) => parentId === null))
    : undefined;
  const designs = (runsQuery.data?.runs ?? []).flatMap((run) =>
    run.designs.map((design) => ({ design, run })),
  );
  const rootDesign = designs.find(({ design }) => design.designId === designId);
  const designName = rootDesign
    ? alphaDesignName(rootDesign.run, rootDesign.design)
    : "Design";
  const adDesigns = designs.filter(({ design }) => design.imageUrl);
  const adNumber =
    adDesigns.findIndex(({ design }) => design.designId === designId) + 1;
  const format = rootDesign?.run.format ?? null;
  const styleTag = rootDesign
    ? ALPHA_MOCKED_STYLES[
        rootDesign.design.planIndex % ALPHA_MOCKED_STYLES.length
      ]!
    : null;

  usePageTitle(`Edit ${designName} - #${requestId}`);

  if (versionsQuery.error) {
    return (
      <p className="p-xl text-sm text-text-error-primary">
        Could not load this design: {versionsQuery.error.message}
      </p>
    );
  }

  if (!versions || !openVersion) return <FullPageLoader />;

  return (
    <EditorWorkspace
      designId={designId}
      designName={designName}
      key={openVersion.id}
      onBack={() =>
        navigate({
          params: { designId, requestId },
          to: "/requests/$requestId/alpha/designs/$designId",
        })
      }
      onBackToAllAds={() =>
        navigate({ params: { requestId }, to: "/requests/$requestId/alpha" })
      }
      onOpenVersion={(version) =>
        navigate({
          params: { designId, requestId },
          search: { version },
          to: "/requests/$requestId/alpha/designs/$designId/edit",
        })
      }
      openVersion={openVersion}
      requestId={requestId}
      safeZone={format?.safeZone ?? null}
      subtitle={
        styleTag && (format ? `${styleTag} · ${format.placement}` : styleTag)
      }
      title={
        adNumber > 0 ? `Edit ad ${adNumber} of ${adDesigns.length}` : "Edit ad"
      }
      versions={versions}
    />
  );
}

type EditorWorkspaceProps = {
  designId: string;
  designName: string;
  onBack: () => void;
  onBackToAllAds: () => void;
  onOpenVersion: (versionId: string) => void;
  openVersion: AlphaDesignVersion;
  requestId: number;
  safeZone: AlphaSafeZone | null;
  subtitle: null | string;
  title: string;
  versions: AlphaDesignVersion[];
};

function EditorWorkspace({
  designId,
  designName,
  onBack,
  onBackToAllAds,
  onOpenVersion,
  openVersion,
  requestId,
  safeZone,
  subtitle,
  title,
  versions,
}: EditorWorkspaceProps) {
  const [history, dispatch] = useReducer(marksReducer, initialMarksHistory);
  const [instructions, setInstructions] = useState("");
  const [tool, setTool] = useState<EditorTool>("box");
  const [showSafeZone, setShowSafeZone] = useState(true);
  const createEdit = useCreateAlphaEdit(requestId, designId, openVersion.id);
  const download = useDownloadAlphaVersion(requestId);

  const { marks } = history;
  const parent = versions.find(({ id }) => id === openVersion.parentId);
  const isEditable = openVersion.status === "generated";
  const hasChanges = marks.length > 0 || instructions.trim() !== "";
  const imageUrl = openVersion.url ?? parent?.url ?? null;

  const addMark = (mark: NewMark) => dispatch({ mark, type: "add" });
  const generate = (editModel?: string) =>
    createEdit.mutate({
      ...(editModel && { model: editModel }),
      instructions: instructions.trim() ? [instructions.trim()] : [],
      marks,
      references: [],
    });

  const disabledReason = isEditable
    ? hasChanges
      ? null
      : "Add instructions or mark the image to apply changes."
    : "This version can't be edited until it has been generated.";

  return (
    <div className="h-full min-h-0 p-6">
      <div className="flex h-full flex-col overflow-hidden rounded-3xl border border-border-secondary bg-bg-primary">
        <header className="flex items-center justify-between gap-4 border-b border-border-secondary px-8 py-5">
          <div className="flex min-w-0 items-center gap-4">
            <Button
              color="gray"
              isIconOnly
              onPress={onBack}
              size="xl"
              variant="secondary"
            >
              <AccessibleIcon aria-label="Back">
                <ChevronLeft />
              </AccessibleIcon>
            </Button>
            <div className="flex min-w-0 flex-col">
              <h1 className="truncate text-xl font-extrabold text-text-primary">
                {title}
              </h1>
              {subtitle && (
                <p className="text-sm font-medium text-text-tertiary">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              color="gray"
              onPress={onBackToAllAds}
              size="xl"
              variant="secondary"
            >
              Back to all ads
            </Button>
            {isEditable && openVersion.url && (
              <Button
                isLoading={download.isPending}
                onPress={() =>
                  download.mutate({
                    fileName: `${designName} v${openVersion.number}`,
                    versionId: openVersion.id,
                  })
                }
                size="xl"
              >
                <Download size={18} />
                Download PNG
              </Button>
            )}
          </div>
        </header>

        <div className="flex min-h-0 flex-1">
          <EditorToolRail
            isDisabled={!isEditable}
            onChangeTool={setTool}
            onRedo={() => dispatch({ type: "redo" })}
            onUndo={() => dispatch({ type: "undo" })}
            tool={tool}
          />

          <div className="flex min-w-0 flex-1 flex-col items-center gap-4 overflow-auto bg-bg-tertiary p-8">
            <div className="flex w-full max-w-[520px] items-center justify-between gap-4">
              <p className="text-sm font-semibold text-text-secondary">
                Drag on the ad to highlight an area.
              </p>
              {safeZone && (
                <SafeZoneToggle
                  isSelected={showSafeZone}
                  onChange={setShowSafeZone}
                />
              )}
            </div>
            <div className="relative w-fit max-w-full shadow-xl">
              {imageUrl && (
                <img
                  alt={designName}
                  className="block max-h-[60vh] max-w-full select-none"
                  draggable={false}
                  src={imageUrl}
                />
              )}
              {safeZone && showSafeZone && (
                <SafeZoneOverlay safeZone={safeZone} />
              )}
              <AnnotationLayer
                isDisabled={!isEditable}
                marks={marks}
                onAddMark={addMark}
                tool={tool}
              />
              {openVersion.status === "generating" && (
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-bg-primary/70 text-text-secondary"
                  role="status"
                >
                  <Loader2 className="animate-spin" size={28} />
                  <span className="text-sm font-medium">
                    Generating this version…
                  </span>
                </div>
              )}
              {openVersion.status === "failed" && (
                <p className="absolute inset-x-0 bottom-0 bg-bg-primary/90 p-3 text-sm text-text-error-primary">
                  This version failed
                  {openVersion.error && `: ${openVersion.error}`}
                </p>
              )}
            </div>
          </div>

          <aside className="flex w-[409px] shrink-0 flex-col gap-5 overflow-y-auto border-l border-border-secondary px-6 pb-7 pt-6">
            <MarksList
              isDisabled={!isEditable}
              marks={marks}
              onChangeInstruction={(n, instruction) =>
                dispatch({ instruction, n, type: "setInstruction" })
              }
              onRemove={(n) => dispatch({ n, type: "remove" })}
            />
            <InstructionsPanel
              isDisabled={!isEditable}
              onChange={setInstructions}
              value={instructions}
            />
            {createEdit.error && (
              <p className="text-sm text-text-error-primary" role="alert">
                {createEdit.error.message}
              </p>
            )}
            <GenerateButton
              changeCount={marks.length}
              isDisabled={!hasChanges || !isEditable}
              isLoading={createEdit.isPending}
              onGenerate={generate}
            />
            {disabledReason && (
              <p className="text-center text-xs text-text-tertiary">
                {disabledReason}
              </p>
            )}
            <VersionsPanel
              onOpen={onOpenVersion}
              openVersion={openVersion}
              versions={versions}
            />
          </aside>
        </div>
      </div>
    </div>
  );
}
